import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { proposalsApi, adminApi, contactsApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import type { Proposal, Contact, PotentialDuplicate } from '@/types';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  User,
  Building2,
  Mail,
  MapPin,
  Globe,
  Loader2,
  Edit3,
  AlertCircle,
  Link2,
  GitMerge
} from 'lucide-react';
import { formatTimeAgo } from "@/utils/timeAgo";

export function ProposalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [existingContact, setExistingContact] = useState<Contact | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContactData, setEditedContactData] = useState<Partial<Contact>>({});

  // Merge-into-existing state. `selectedCandidateId` is the master contact
  // id the reviewer picked from the comparison panel; `mergeBanner` is the
  // success/error result shown after the API call returns.
  const [selectedCandidateId, setSelectedCandidateId] = useState<number | null>(null);
  const [isMerging, setIsMerging] = useState(false);
  const [mergeBanner, setMergeBanner] = useState<{ kind: 'success' | 'warning' | 'error'; message: string } | null>(null);

  useEffect(() => {
    loadProposal();
  }, [id]);

  const loadProposal = async () => {
    setIsLoading(true);
    try {
      const response = await proposalsApi.getById(Number(id));
      const proposalData = response.data.data;
      setProposal(proposalData);
      setEditedContactData(proposalData.new_contact_data);
      
      // Load existing contact for comparison if it's an update
      if (proposalData.contact_id && proposalData.action === 'update') {
        try {
          const contactResponse = await contactsApi.getById(proposalData.contact_id);
          setExistingContact(contactResponse.data.data);
        } catch (e) {
          console.error('Failed to load existing contact:', e);
        }
      }
    } catch (error) {
      console.error('Failed to load proposal:', error);
      setError('Failed to load proposal');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!isAdmin) return;
    setIsProcessing(true);
    try {
      await adminApi.approveProposal(Number(id), reviewNotes);
      navigate('/proposals');
    } catch (error) {
      console.error('Failed to approve:', error);
      setError('Failed to approve proposal');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!isAdmin) return;
    setIsProcessing(true);
    try {
      await adminApi.rejectProposal(Number(id), reviewNotes);
      navigate('/proposals');
    } catch (error) {
      console.error('Failed to reject:', error);
      setError('Failed to reject proposal');
    } finally {
      setIsProcessing(false);
    }
  };
  const handleApproveWithChanges = async () => {
    if (!isAdmin) return;
    setIsProcessing(true);
    try {
      await adminApi.approveProposalWithChanges(Number(id), editedContactData, reviewNotes);
      navigate('/proposals');
    } catch (error) {
      console.error('Failed to approve with changes:', error);
      setError('Failed to approve proposal with changes');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMerge = async () => {
    if (!isAdmin || selectedCandidateId === null) return;
    const candidate = (proposal?.potential_duplicates ?? []).find(c => c.id === selectedCandidateId);
    if (!candidate) return;
    const confirmMsg = `Diese Aktion führt den Antrag mit Kontakt #${candidate.id} (${(candidate.first_name || '')} ${(candidate.last_name || '')}) zusammen und entfernt den doppelten Outlook-Eintrag${proposal?.new_contact_data?.source_user_email ? ` aus dem Postfach von ${proposal.new_contact_data.source_user_email}` : ''}. Fortfahren?`;
    if (!window.confirm(confirmMsg)) return;
    setIsMerging(true);
    setMergeBanner(null);
    try {
      const res = await adminApi.mergeProposalIntoExisting(Number(id), selectedCandidateId, reviewNotes || undefined);
      const data = (res as any)?.data?.data ?? {};
      const outlookOk = data.outlook_delete === true;
      const outlookAttempted = data.outlook_attempted === true;
      let msg = `Mit Kontakt #${selectedCandidateId} zusammengeführt.`;
      if (outlookOk) {
        msg += ' Der doppelte Outlook-Eintrag wurde aus dem Postfach entfernt.';
      } else if (outlookAttempted) {
        msg += ' Outlook-Bereinigung fehlgeschlagen — bitte manuell prüfen.';
      } else {
        msg += ' Kein Outlook-Kontakt zum Aufräumen vorhanden.';
      }
      setMergeBanner({ kind: outlookOk || !outlookAttempted ? 'success' : 'warning', message: msg });
      // Auto-navigate back to the list after a short pause so the reviewer
      // can read the success message.
      setTimeout(() => navigate('/proposals'), 2500);
    } catch (err: any) {
      console.error('Failed to merge:', err);
      const backendMsg = err?.response?.data?.message || err?.message || 'Unbekannter Fehler';
      setMergeBanner({ kind: 'error', message: `Zusammenführung fehlgeschlagen: ${backendMsg}` });
    } finally {
      setIsMerging(false);
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'create': return { text: 'Neuer Kontakt', color: 'bg-green-100 text-green-800' };
      case 'update': return { text: 'Aktualisierung', color: 'bg-yellow-100 text-yellow-800' };
      case 'delete': return { text: 'Löschung', color: 'bg-red-100 text-red-800' };
      default: return { text: action, color: 'bg-gray-100 text-gray-800' };
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return { text: 'Ausstehend', color: 'bg-yellow-100 text-yellow-800' };
      case 'approved': return { text: 'Genehmigt', color: 'bg-green-100 text-green-800' };
      case 'rejected': return { text: 'Abgelehnt', color: 'bg-red-100 text-red-800' };
      default: return { text: status, color: 'bg-gray-100 text-gray-800' };
    }
  };

  const renderFieldComparison = (label: string, oldValue?: string, newValue?: string) => {
    if (!newValue && !oldValue) return null;
    const hasChanged = oldValue !== newValue && newValue;
    
    return (
      <div className={`py-2 px-3 rounded-md ${hasChanged ? 'bg-yellow-50 border border-yellow-200' : ''}`}>
        <span className="text-sm font-medium text-gray-700">{label}:</span>
        <div className="mt-1">
          {oldValue && hasChanged && (
            <span className="text-sm text-gray-400 line-through mr-2">{oldValue}</span>
          )}
          {newValue && (
            <span className={`text-sm ${hasChanged ? 'text-green-700 font-medium' : 'text-gray-900'}`}>
              {newValue}
            </span>
          )}
          {!newValue && oldValue && (
            <span className="text-sm text-red-500 italic">(wird gelöscht)</span>
          )}
        </div>
      </div>
    );
  };

  const renderContactData = (data: Partial<Contact>, isComparison = false) => {
    return (
      <div className="space-y-6">
        {/* Name Section */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center">
            <User className="h-4 w-4 mr-2" />
            Name
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {isComparison && existingContact ? (
              <>
                {renderFieldComparison('Anrede', existingContact.title, data.title)}
                {renderFieldComparison('Vorname', existingContact.first_name, data.first_name)}
                {renderFieldComparison('Nachname', existingContact.last_name, data.last_name)}
                {renderFieldComparison('Anzeigename', existingContact.display_name, data.display_name)}
              </>
            ) : (
              <>
                {data.title && <div className="py-2"><span className="text-sm text-gray-500">Anrede:</span><p className="text-sm text-gray-900">{data.title}</p></div>}
                {data.first_name && <div className="py-2"><span className="text-sm text-gray-500">Vorname:</span><p className="text-sm text-gray-900">{data.first_name}</p></div>}
                {data.last_name && <div className="py-2"><span className="text-sm text-gray-500">Nachname:</span><p className="text-sm text-gray-900">{data.last_name}</p></div>}
                {data.display_name && <div className="py-2"><span className="text-sm text-gray-500">Anzeigename:</span><p className="text-sm text-gray-900">{data.display_name}</p></div>}
              </>
            )}
          </div>
        </div>

        {/* Company Section */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center">
            <Building2 className="h-4 w-4 mr-2" />
            Unternehmen
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {isComparison && existingContact ? (
              <>
                {renderFieldComparison('Firma', existingContact.company_name, data.company_name)}
                {renderFieldComparison('Abteilung', existingContact.department, data.department)}
                {renderFieldComparison('Position', existingContact.job_title, data.job_title)}
              </>
            ) : (
              <>
                {data.company_name && <div className="py-2"><span className="text-sm text-gray-500">Firma:</span><p className="text-sm text-gray-900">{data.company_name}</p></div>}
                {data.department && <div className="py-2"><span className="text-sm text-gray-500">Abteilung:</span><p className="text-sm text-gray-900">{data.department}</p></div>}
                {data.job_title && <div className="py-2"><span className="text-sm text-gray-500">Position:</span><p className="text-sm text-gray-900">{data.job_title}</p></div>}
              </>
            )}
          </div>
        </div>

        {/* Contact Section */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center">
            <Mail className="h-4 w-4 mr-2" />
            Kontakt
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {isComparison && existingContact ? (
              <>
                {renderFieldComparison('E-Mail', existingContact.email_address, data.email_address)}
                {renderFieldComparison('E-Mail 2', existingContact.secondary_email, data.secondary_email)}
                {renderFieldComparison('E-Mail 3', existingContact.tertiary_email, data.tertiary_email)}
                {renderFieldComparison('Mobil', existingContact.mobile_phone, data.mobile_phone)}
                {renderFieldComparison('Geschäftlich', existingContact.business_phone, data.business_phone)}
                {renderFieldComparison('Geschäftlich 2', existingContact.business_phone_2, data.business_phone_2)}
                {renderFieldComparison('Privat', existingContact.home_phone, data.home_phone)}
                {renderFieldComparison('Privat 2', existingContact.home_phone_2, data.home_phone_2)}
                {renderFieldComparison('Fax', existingContact.fax, data.fax)}
                {renderFieldComparison('Pager', existingContact.pager, data.pager)}
              </>
            ) : (
              <>
                {data.email_address && <div className="py-2"><span className="text-sm text-gray-500">E-Mail:</span><p className="text-sm text-gray-900">{data.email_address}</p></div>}
                {data.secondary_email && <div className="py-2"><span className="text-sm text-gray-500">E-Mail 2:</span><p className="text-sm text-gray-900">{data.secondary_email}</p></div>}
                {data.tertiary_email && <div className="py-2"><span className="text-sm text-gray-500">E-Mail 3:</span><p className="text-sm text-gray-900">{data.tertiary_email}</p></div>}
                {data.mobile_phone && <div className="py-2"><span className="text-sm text-gray-500">Mobil:</span><p className="text-sm text-gray-900">{data.mobile_phone}</p></div>}
                {data.business_phone && <div className="py-2"><span className="text-sm text-gray-500">Geschäftlich:</span><p className="text-sm text-gray-900">{data.business_phone}</p></div>}
                {data.business_phone_2 && <div className="py-2"><span className="text-sm text-gray-500">Geschäftlich 2:</span><p className="text-sm text-gray-900">{data.business_phone_2}</p></div>}
                {data.home_phone && <div className="py-2"><span className="text-sm text-gray-500">Privat:</span><p className="text-sm text-gray-900">{data.home_phone}</p></div>}
                {data.home_phone_2 && <div className="py-2"><span className="text-sm text-gray-500">Privat 2:</span><p className="text-sm text-gray-900">{data.home_phone_2}</p></div>}
                {data.fax && <div className="py-2"><span className="text-sm text-gray-500">Fax:</span><p className="text-sm text-gray-900">{data.fax}</p></div>}
                {data.pager && <div className="py-2"><span className="text-sm text-gray-500">Pager:</span><p className="text-sm text-gray-900">{data.pager}</p></div>}
              </>
            )}
          </div>
        </div>

        {/* Address Section */}
        {(data.street || data.city || (isComparison && existingContact?.street)) && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center">
              <MapPin className="h-4 w-4 mr-2" />
              Adresse
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {isComparison && existingContact ? (
                <>
                  {renderFieldComparison('Straße', existingContact.street, data.street)}
                  {renderFieldComparison('PLZ', existingContact.postal_code, data.postal_code)}
                  {renderFieldComparison('Stadt', existingContact.city, data.city)}
                  {renderFieldComparison('Bundesland', existingContact.state, data.state)}
                  {renderFieldComparison('Land', existingContact.country, data.country)}
                </>
              ) : (
                <>
                  {data.street && <div className="py-2"><span className="text-sm text-gray-500">Straße:</span><p className="text-sm text-gray-900">{data.street}</p></div>}
                  {data.postal_code && <div className="py-2"><span className="text-sm text-gray-500">PLZ:</span><p className="text-sm text-gray-900">{data.postal_code}</p></div>}
                  {data.city && <div className="py-2"><span className="text-sm text-gray-500">Stadt:</span><p className="text-sm text-gray-900">{data.city}</p></div>}
                  {data.state && <div className="py-2"><span className="text-sm text-gray-500">Bundesland:</span><p className="text-sm text-gray-900">{data.state}</p></div>}
                  {data.country && <div className="py-2"><span className="text-sm text-gray-500">Land:</span><p className="text-sm text-gray-900">{data.country}</p></div>}
                </>
              )}
            </div>
          </div>
        )}

        {/* Home Address Section */}
        {(data.home_street || data.home_city || (isComparison && existingContact?.home_street)) && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center">
              <MapPin className="h-4 w-4 mr-2" />
              Adresse Privat
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {isComparison && existingContact ? (
                <>
                  {renderFieldComparison('Straße', existingContact.home_street, data.home_street)}
                  {renderFieldComparison('PLZ', existingContact.home_postal_code, data.home_postal_code)}
                  {renderFieldComparison('Stadt', existingContact.home_city, data.home_city)}
                  {renderFieldComparison('Bundesland', existingContact.home_state, data.home_state)}
                  {renderFieldComparison('Land', existingContact.home_country, data.home_country)}
                </>
              ) : (
                <>
                  {data.home_street && <div className="py-2"><span className="text-sm text-gray-500">Straße:</span><p className="text-sm text-gray-900">{data.home_street}</p></div>}
                  {data.home_postal_code && <div className="py-2"><span className="text-sm text-gray-500">PLZ:</span><p className="text-sm text-gray-900">{data.home_postal_code}</p></div>}
                  {data.home_city && <div className="py-2"><span className="text-sm text-gray-500">Stadt:</span><p className="text-sm text-gray-900">{data.home_city}</p></div>}
                  {data.home_state && <div className="py-2"><span className="text-sm text-gray-500">Bundesland:</span><p className="text-sm text-gray-900">{data.home_state}</p></div>}
                  {data.home_country && <div className="py-2"><span className="text-sm text-gray-500">Land:</span><p className="text-sm text-gray-900">{data.home_country}</p></div>}
                </>
              )}
            </div>
          </div>
        )}

        {/* Bauvorhaben Address Section */}
        {(data.other_street || data.other_city || (isComparison && existingContact?.other_street)) && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center">
              <MapPin className="h-4 w-4 mr-2" />
              Bauvorhaben Adresse
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {isComparison && existingContact ? (
                <>
                  {renderFieldComparison('Straße', existingContact.other_street, data.other_street)}
                  {renderFieldComparison('PLZ', existingContact.other_postal_code, data.other_postal_code)}
                  {renderFieldComparison('Stadt', existingContact.other_city, data.other_city)}
                  {renderFieldComparison('Bundesland', existingContact.other_state, data.other_state)}
                  {renderFieldComparison('Land', existingContact.other_country, data.other_country)}
                </>
              ) : (
                <>
                  {data.other_street && <div className="py-2"><span className="text-sm text-gray-500">Straße:</span><p className="text-sm text-gray-900">{data.other_street}</p></div>}
                  {data.other_postal_code && <div className="py-2"><span className="text-sm text-gray-500">PLZ:</span><p className="text-sm text-gray-900">{data.other_postal_code}</p></div>}
                  {data.other_city && <div className="py-2"><span className="text-sm text-gray-500">Stadt:</span><p className="text-sm text-gray-900">{data.other_city}</p></div>}
                  {data.other_state && <div className="py-2"><span className="text-sm text-gray-500">Bundesland:</span><p className="text-sm text-gray-900">{data.other_state}</p></div>}
                  {data.other_country && <div className="py-2"><span className="text-sm text-gray-500">Land:</span><p className="text-sm text-gray-900">{data.other_country}</p></div>}
                </>
              )}
            </div>
          </div>
        )}

        {/* Other Info */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center">
            <Globe className="h-4 w-4 mr-2" />
            Weitere Informationen
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {isComparison && existingContact ? (
              <>
                {renderFieldComparison('Webseite', existingContact.business_homepage, data.business_homepage)}
                {renderFieldComparison('Geburtstag', existingContact.birthday, data.birthday)}
                {renderFieldComparison('Notizen', existingContact.personal_notes, data.personal_notes)}
                {data.personal_notes && !existingContact?.personal_notes && (
                  <div className="col-span-2 py-2">
                    <span className="text-sm text-gray-500">Notizen:</span>
                    <div className="mt-1 p-2 bg-gray-50 rounded text-sm text-gray-700 whitespace-pre-wrap">
                      {data.personal_notes}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {data.business_homepage && <div className="py-2"><span className="text-sm text-gray-500">Webseite:</span><p className="text-sm text-gray-900">{data.business_homepage}</p></div>}
                {data.birthday && <div className="py-2"><span className="text-sm text-gray-500">Geburtstag:</span><p className="text-sm text-gray-900">{data.birthday}</p></div>}
                {data.personal_notes && (
                  <div className="col-span-2 py-2">
                    <span className="text-sm text-gray-500">Notizen:</span>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{data.personal_notes}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Antrag nicht gefunden</p>
        <button
          onClick={() => navigate('/proposals')}
          className="mt-4 text-primary-600 hover:text-primary-700"
        >
          Zurück zu Anträgen
        </button>
      </div>
    );
  }

  const actionLabel = getActionLabel(proposal.action);
  const statusLabel = getStatusLabel(proposal.status);
  const isPending = proposal.status === 'pending';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurück
        </button>
        
        {isPending && isAdmin && (
          <div className="flex flex-wrap gap-2">
          <button
            onClick={handleApprove}
            disabled={isProcessing}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {isProcessing ? 'Wird genehmigt...' : 'Genehmigen'}
          </button>
          <button
            onClick={handleReject}
            disabled={isProcessing}
            className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Ablehnen
          </button>
          <button
            onClick={() => setIsEditing(!isEditing)}
            disabled={isProcessing}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            <Edit3 className="h-4 w-4 mr-2" />
            {isEditing ? "Bearbeiten beenden" : "Bearbeiten"}
          </button>
          {isEditing && (
            <button
              onClick={handleApproveWithChanges}
              disabled={isProcessing}
              className="inline-flex items-center px-4 py-2 bg-accent-600 text-white rounded-md hover:bg-accent-700 disabled:opacity-50"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {isProcessing ? "Wird gespeichert..." : "Speichern & Genehmigen"}
            </button>
          )}
          {/* Merge-into-existing button. Only enabled when the reviewer
              has actually picked a candidate from the comparison panel
              below. The candidate list is empty for delete proposals
              (handled by the panel render logic) and for proposals with
              no matches at all. */}
          {proposal.action !== 'delete' && (proposal.potential_duplicates?.length ?? 0) > 0 && (
            <button
              onClick={handleMerge}
              disabled={isProcessing || isMerging || selectedCandidateId === null}
              className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title={selectedCandidateId === null ? 'Bitte zuerst einen Kandidaten unten auswählen' : 'Mit dem ausgewählten bestehenden Kontakt zusammenführen'}
            >
              <GitMerge className="h-4 w-4 mr-2" />
              {isMerging ? 'Wird zusammengeführt…' : 'Mit bestehendem Kontakt zusammenführen'}
            </button>
          )}
    </div>
      )}
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {mergeBanner && (
        <div className={`px-4 py-3 rounded-md border ${
          mergeBanner.kind === 'success' ? 'bg-green-50 border-green-200 text-green-800'
            : mergeBanner.kind === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {mergeBanner.message}
        </div>
      )}

      {/* Proposal Info Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${actionLabel.color}`}>
            {actionLabel.text}
          </span>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusLabel.color}`}>
            {statusLabel.text}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Eingereicht von:</span>
            <p className="font-medium text-gray-900">{proposal.proposed_by_name || proposal.proposed_by_email}</p>
          </div>
          <div>
            <span className="text-gray-500">Datum:</span>
            <p className="text-gray-900">
              {formatTimeAgo(proposal.created_at)}
            </p>
          </div>
        </div>

        {isPending && isAdmin && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prüfungsnotizen (optional)
            </label>
            <textarea
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder="Notizen zur Prüfung..."
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              rows={2}
            />
          </div>
        )}
      </div>

      {/* Duplicate-warning banner + comparison panel. Only rendered when
          the backend returned at least one candidate. Delete proposals
          don't make sense to merge, so we show a softer note instead.

          We render this regardless of proposal.status so reviewers can still
          see the side-by-side on already-approved/rejected proposals
          (useful when double-checking a previous decision). The merge
          button itself stays gated on `isPending` further up. */}
      {proposal.action === 'delete' && (proposal.potential_duplicates?.length ?? 0) > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">
                Hinweis: {proposal.potential_duplicates!.length} ähnliche(r) Kontakt(e) gefunden.
              </p>
              <p className="text-sm text-yellow-700 mt-1">
                Löschanträge können nicht zusammengeführt werden. Bitte prüfe die unten aufgeführten Kontakte separat, bevor du diesen Antrag bearbeitest.
              </p>

              <div className="mt-4 space-y-3">
                {proposal.potential_duplicates!.map(candidate => (
                  <CandidateCard
                    key={candidate.id}
                    candidate={candidate}
                    proposed={isEditing ? editedContactData : proposal.new_contact_data}
                    selected={selectedCandidateId === candidate.id}
                    onSelect={() => setSelectedCandidateId(candidate.id)}
                    showRadio={false}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {proposal.action !== 'delete' && (proposal.potential_duplicates?.length ?? 0) > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Link2 className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-800">
                Achtung: möglicherweise ein Duplikat. Wir haben {proposal.potential_duplicates!.length} ähnliche(n) Kontakt(e) in der Datenbank gefunden.
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Bitte prüfe unten, ob dieser Antrag mit einem bestehenden Kontakt zusammengeführt werden soll. Wähle dazu einen Kandidaten aus — die Daten werden dann gemerged (NULL-Werte im Ziel werden gefüllt) und der doppelte Outlook-Eintrag wird aus dem Postfach des Antragstellers entfernt.
              </p>

              <div className="mt-4 space-y-3">
                {proposal.potential_duplicates!.map(candidate => (
                  <CandidateCard
                    key={candidate.id}
                    candidate={candidate}
                    proposed={isEditing ? editedContactData : proposal.new_contact_data}
                    selected={selectedCandidateId === candidate.id}
                    onSelect={() => setSelectedCandidateId(candidate.id)}
                    showRadio={true}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Data */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {proposal.action === 'update' && existingContact ? (
            <span className="flex items-center">
              <Edit3 className="h-5 w-5 mr-2" />
              Änderungsübersicht
            </span>
          ) : (
            <span className="flex items-center">
              <User className="h-5 w-5 mr-2" />
              Kontaktdaten
            </span>
          )}
        </h2>
        
        {proposal.action === 'update' && existingContact && (
          <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-md">
            <p className="text-sm text-primary-700 flex items-center">
              <AlertCircle className="h-4 w-4 mr-2" />
              Gelbe Felder zeigen Änderungen. Durchgestrichene Werte werden ersetzt.
            </p>
          </div>
        )}


        {isEditing && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
            <h3 className="text-sm font-semibold text-primary-900 mb-3">Kontaktdaten bearbeiten</h3>
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Name</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-gray-700">Anrede</label>
                  <input type="text" value={editedContactData.title || ''} onChange={(e) => setEditedContactData({...editedContactData, title: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Vorname</label>
                  <input type="text" value={editedContactData.first_name || ''} onChange={(e) => setEditedContactData({...editedContactData, first_name: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Nachname</label>
                  <input type="text" value={editedContactData.last_name || ''} onChange={(e) => setEditedContactData({...editedContactData, last_name: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-500">Anzeigename</label>
                  <input type="text" disabled value={[editedContactData.first_name, editedContactData.last_name].filter(Boolean).join(' ').trim() || ''} className="mt-1 block w-full rounded-md border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed shadow-sm text-sm" /></div>
              </div>
            </div>
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Unternehmen</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-gray-700">Firma</label>
                  <input type="text" value={editedContactData.company_name || ''} onChange={(e) => setEditedContactData({...editedContactData, company_name: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Abteilung</label>
                  <input type="text" value={editedContactData.department || ''} onChange={(e) => setEditedContactData({...editedContactData, department: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Position</label>
                  <input type="text" value={editedContactData.job_title || ''} onChange={(e) => setEditedContactData({...editedContactData, job_title: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
              </div>
            </div>
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">E-Mail</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-gray-700">Primaere E-Mail</label>
                  <input type="email" value={editedContactData.email_address || ''} onChange={(e) => setEditedContactData({...editedContactData, email_address: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Sekundaere E-Mail</label>
                  <input type="email" value={editedContactData.secondary_email || ''} onChange={(e) => setEditedContactData({...editedContactData, secondary_email: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
              </div>
            </div>
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Telefon</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-gray-700">Mobil</label>
                  <input type="text" value={editedContactData.mobile_phone || ''} onChange={(e) => setEditedContactData({...editedContactData, mobile_phone: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Geschaeftlich</label>
                  <input type="text" value={editedContactData.business_phone || ''} onChange={(e) => setEditedContactData({...editedContactData, business_phone: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Geschaeftlich 2</label>
                  <input type="text" value={editedContactData.business_phone_2 || ''} onChange={(e) => setEditedContactData({...editedContactData, business_phone_2: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Privat</label>
                  <input type="text" value={editedContactData.home_phone || ''} onChange={(e) => setEditedContactData({...editedContactData, home_phone: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Fax</label>
                  <input type="text" value={editedContactData.fax || ''} onChange={(e) => setEditedContactData({...editedContactData, fax: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
              </div>
            </div>
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Geschaeftsadresse</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><label className="block text-xs font-medium text-gray-700">Strasse</label>
                  <input type="text" value={editedContactData.street || ''} onChange={(e) => setEditedContactData({...editedContactData, street: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">PLZ</label>
                  <input type="text" value={editedContactData.postal_code || ''} onChange={(e) => setEditedContactData({...editedContactData, postal_code: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Stadt</label>
                  <input type="text" value={editedContactData.city || ''} onChange={(e) => setEditedContactData({...editedContactData, city: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Bundesland</label>
                  <input type="text" value={editedContactData.state || ''} onChange={(e) => setEditedContactData({...editedContactData, state: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Land</label>
                  <input type="text" value={editedContactData.country || ''} onChange={(e) => setEditedContactData({...editedContactData, country: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
              </div>
            </div>
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Privatadresse</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><label className="block text-xs font-medium text-gray-700">Strasse</label>
                  <input type="text" value={editedContactData.home_street || ''} onChange={(e) => setEditedContactData({...editedContactData, home_street: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">PLZ</label>
                  <input type="text" value={editedContactData.home_postal_code || ''} onChange={(e) => setEditedContactData({...editedContactData, home_postal_code: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Stadt</label>
                  <input type="text" value={editedContactData.home_city || ''} onChange={(e) => setEditedContactData({...editedContactData, home_city: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Bundesland</label>
                  <input type="text" value={editedContactData.home_state || ''} onChange={(e) => setEditedContactData({...editedContactData, home_state: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Land</label>
                  <input type="text" value={editedContactData.home_country || ''} onChange={(e) => setEditedContactData({...editedContactData, home_country: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
              </div>
            </div>
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Weitere Informationen</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-gray-700">Webseite</label>
                  <input type="url" value={editedContactData.business_homepage || ''} onChange={(e) => setEditedContactData({...editedContactData, business_homepage: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-700">Geburtstag</label>
                  <input type="date" value={editedContactData.birthday || ''} onChange={(e) => setEditedContactData({...editedContactData, birthday: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm" /></div>
              </div>
            </div>
          </div>
        )}
        {renderContactData(isEditing ? editedContactData : proposal.new_contact_data, proposal.action === 'update')}
      </div>
    </div>
  );
}

/**
 * A single candidate row in the duplicate-warning comparison panel.
 *
 * Shows the candidate's identity (name, company, email, phone), the
 * `match_reasons` (e.g. "email:primary", "phone:mobile") so the reviewer
 * can see *why* the match was suggested, and a compact side-by-side
 * preview of the fields that differ. The "after merge" column shows the
 * values the merge will actually produce using the same NULL-backfill-
 * first rule as the backend (`AdminController::approveUpdate`).
 */
function CandidateCard({
  candidate,
  proposed,
  selected,
  onSelect,
  showRadio = true,
}: {
  candidate: PotentialDuplicate;
  proposed: Partial<Contact>;
  selected: boolean;
  onSelect: () => void;
  /** Whether to render the merge-radio. Off for delete proposals where
   *  the button that consumes the selection isn't shown. */
  showRadio?: boolean;
}) {
  // "Strong" matches are email-based (80+) or email+phone (100). Pure
  // phone matches at 60 still show but in the softer yellow tone — too
  // many home-phone collisions are coincidence to merit the strong
  // warning treatment.
  const strong = candidate.match_score >= 80;

  // Build a quick lookup of "match reason → which column matched".
  // The candidate payload only carries one row per phone type, but the
  // backend can match on any of `mobile_phone`, `business_phone`,
  // `business_phone_2`, `home_phone`, `home_phone_2`. We expand the
  // side-by-side to show every column the matcher looked at, and use
  // the reason list to mark the actual culprit with a stronger highlight
  // so the reviewer can see *why* this candidate was flagged.
  const matchedPhoneKinds = new Set(
    candidate.match_reasons
      .filter((r) => r.startsWith('phone:'))
      .map((r) => r.slice('phone:'.length))
  );
  const matchedEmailKinds = new Set(
    candidate.match_reasons
      .filter((r) => r.startsWith('email:'))
      .map((r) => r.slice('email:'.length))
  );

  const fields: Array<{
    label: string;
    cur: string | null | undefined;
    nw:  string | null | undefined;
    /** Tag used to highlight this row when it's the column that fired the match. */
    matchTag?: string;
  }> = [
    { label: 'Vorname',         cur: candidate.first_name,      nw: proposed.first_name },
    { label: 'Nachname',        cur: candidate.last_name,       nw: proposed.last_name },
    { label: 'Firma',           cur: candidate.company_name,    nw: proposed.company_name },
    { label: 'E-Mail',          cur: candidate.email_address,   nw: proposed.email_address,    matchTag: matchedEmailKinds.has('primary')   ? 'primary'   : undefined },
    { label: 'E-Mail 2',        cur: candidate.secondary_email, nw: proposed.secondary_email,  matchTag: matchedEmailKinds.has('secondary') ? 'secondary' : undefined },
    { label: 'E-Mail 3',        cur: candidate.tertiary_email,  nw: proposed.tertiary_email,   matchTag: matchedEmailKinds.has('tertiary')  ? 'tertiary'  : undefined },
    { label: 'Mobil',           cur: candidate.mobile_phone,    nw: proposed.mobile_phone,     matchTag: matchedPhoneKinds.has('mobile')     ? 'mobile'     : undefined },
    { label: 'Geschäftlich',    cur: candidate.business_phone,  nw: proposed.business_phone,   matchTag: matchedPhoneKinds.has('business')   ? 'business'   : undefined },
    { label: 'Geschäftlich 2',  cur: candidate.business_phone_2,nw: proposed.business_phone_2, matchTag: matchedPhoneKinds.has('business_2') ? 'business_2' : undefined },
    { label: 'Privat',          cur: candidate.home_phone,      nw: proposed.home_phone,       matchTag: matchedPhoneKinds.has('home')       ? 'home'       : undefined },
    { label: 'Privat 2',        cur: candidate.home_phone_2,    nw: proposed.home_phone_2,     matchTag: matchedPhoneKinds.has('home_2')     ? 'home_2'     : undefined },
  ];

  return (
    <div
      className={`block rounded-md border p-3 transition-colors ${
        showRadio
          ? 'cursor-pointer'
          : ''
      } ${
        selected
          ? 'border-amber-500 bg-white ring-2 ring-amber-200'
          : 'border-amber-200 bg-white hover:border-amber-400'
      }`}
      onClick={showRadio ? onSelect : undefined}
    >
      <div className="flex items-start gap-3">
        {showRadio && (
          <input
            type="radio"
            name="merge-candidate"
            checked={selected}
            onChange={onSelect}
            className="mt-1 h-4 w-4 text-amber-600 border-gray-300 focus:ring-amber-500"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-semibold text-gray-900">
              {candidate.first_name || ''} {candidate.last_name || ''}
            </span>
            {candidate.company_name && (
              <span className="text-sm text-gray-600">— {candidate.company_name}</span>
            )}
            <span className={`text-xs px-1.5 py-0.5 rounded ${
              strong ? 'bg-amber-100 text-amber-800' : 'bg-yellow-50 text-yellow-800'
            }`}>
              Match: {candidate.match_score} ({candidate.match_reasons.join(', ')})
            </span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5">Master-ID: #{candidate.id}</div>

          {/* Always-on side-by-side diff. The radio is only used to pick
              which candidate gets merged, so the comparison should be
              visible without an extra click — that's the whole point. */}
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="pr-3 font-medium">Feld</th>
                  <th className="pr-3 font-medium">Aktuell (Datenbank)</th>
                  <th className="pr-3 font-medium">Antrag</th>
                  <th className="pr-3 font-medium">Nach Merge</th>
                </tr>
              </thead>
              <tbody>
                {fields.map(({ label, cur, nw, matchTag }) => {
                  const curS = (cur ?? '') as string;
                  const nwS  = (nw ?? '') as string;
                  // Skip rows where both sides are empty — they add no signal
                  // and just bloat the table for proposals with sparse data.
                  if (!curS && !nwS) return null;
                  const after = (curS && curS.length > 0) ? curS : nwS;
                  const differs = curS !== nwS;
                  // Stronger highlight for the row that actually matched;
                  // weaker for any other row that just happens to differ.
                  const rowClass = matchTag
                    ? 'bg-amber-200 ring-1 ring-amber-400'
                    : differs ? 'bg-amber-50' : '';
                  return (
                    <tr key={label} className={rowClass}>
                      <td className="pr-3 py-1 font-medium text-gray-700">{label}</td>
                      <td className="pr-3 py-1 text-gray-900">{curS || <span className="text-gray-400 italic">leer</span>}</td>
                      <td className="pr-3 py-1 text-gray-900">{nwS || <span className="text-gray-400 italic">leer</span>}</td>
                      <td className="pr-3 py-1 text-amber-800 font-medium">{after || <span className="text-gray-400 italic">leer</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
